/**
 * Round 6 (WP3): where a run is and how it ended, from RunDetailApi. Pure; shared by the run
 * notification (distributionRunWatcher.ts) and the run page's live progress card, so both say the
 * same thing.
 */
import type { NotificationInput } from "../notify";
import { distributionNotices as N, distributionRunNoticeId, type DistributionRunPhase } from "./distributionNotifications";
import type { DeliveryApi, DeliveryStatusApi, RunDetailApi } from "./distributions.types";

const FINISHED: readonly DeliveryStatusApi[] = ["sent", "failed", "skipped", "held"];

export interface RunProgressView {
  /** `done` once the run is over. */
  phase: DistributionRunPhase | "done";
  /** Copies made so far, of `copies`. */
  made: number;
  copies: number;
  /** Deliveries finished (sent, failed, skipped or held), of `deliveries`. */
  finished: number;
  deliveries: number;
}

export const isRunOver = (detail: Pick<RunDetailApi, "run">): boolean =>
  detail.run.status !== "queued" && detail.run.status !== "running";

export const runProgressOf = ({ run, slices, deliveries }: RunDetailApi): RunProgressView => {
  const made = slices.filter((slice) => slice.status !== "pending" && slice.status !== "rendering").length;
  const finished = deliveries.filter((delivery) => FINISHED.includes(delivery.status)).length;
  const base = { made, copies: slices.length, finished, deliveries: deliveries.length };
  if (run.status !== "queued" && run.status !== "running") return { ...base, phase: "done" };
  if (run.status === "queued" || slices.length === 0) return { ...base, phase: "check" };
  return { ...base, phase: made < slices.length ? "make" : "send" };
};

/** A sent delivery's `error` is only a note (one format left out): a warning, not a failure. */
export const isWarnedDelivery = (delivery: DeliveryApi): boolean => delivery.status === "sent" && Boolean(delivery.error);

export interface RunOutcome {
  sent: number;
  total: number;
  copies: number;
  failed: number;
  held: number;
  warned: number;
  /** The first reason a delivery or the run gave, verbatim. */
  reason: string | null;
  /** Nothing went out although there was someone to send to, or the run itself failed. */
  sentNothing: boolean;
}

export const runOutcomeOf = ({ run, deliveries, slices }: RunDetailApi): RunOutcome => {
  const count = (status: DeliveryStatusApi) => deliveries.filter((delivery) => delivery.status === status).length;
  const sent = count("sent");
  const firstProblem = deliveries.find((delivery) => delivery.status === "failed" || delivery.status === "held");
  return {
    sent,
    total: deliveries.length,
    copies: run.counts.copies || slices.filter((slice) => slice.status === "built" || slice.status === "rendered").length,
    failed: count("failed"),
    held: count("held"),
    warned: deliveries.filter(isWarnedDelivery).length,
    reason: run.error ?? firstProblem?.error ?? null,
    sentNothing: run.status === "failed" || (sent === 0 && deliveries.length > 0),
  };
};

export interface RunNoticeOptions {
  /** The distribution's name. */
  name: string;
  /** The run page, for "View run". */
  path?: string;
  /** "Tue, Oct 27 at 8:00 AM PT", when it runs again. */
  next?: string | null;
  /** "you", or where a test went. */
  testSentTo?: string;
}

/** The notification for a run as it stands: started, a progress tick, or its result. */
export const runNoticeOf = (detail: RunDetailApi, options: RunNoticeOptions): NotificationInput => {
  const { run } = detail;
  const context = { runId: run.id, name: options.name, path: options.path, startedAt: run.started_at, queuedAt: run.queued_at, finishedAt: run.finished_at };
  const progress = runProgressOf(detail);
  if (progress.phase === "check") return run.trigger === "schedule" ? N.scheduleFired(context) : N.runStarted(context);
  if (progress.phase === "make") return N.runProgress({ ...context, phase: "make", done: progress.made, total: progress.copies });
  if (progress.phase === "send") return N.runProgress({ ...context, phase: "send", done: progress.finished, total: progress.deliveries });
  const outcome = runOutcomeOf(detail);
  if (outcome.sentNothing) return N.runFailed({ ...context, reason: outcome.reason });
  if (run.trigger === "test") {
    const first = detail.deliveries[0];
    const sent = N.testSent({ sentTo: options.testSentTo, person: first?.recipient.name, value: first?.recipient.value, path: options.path });
    return { ...sent, id: distributionRunNoticeId(run.id) };
  }
  if (outcome.failed || outcome.held || outcome.warned) return N.runFinishedWithProblems({ ...context, ...outcome, next: options.next });
  return N.runCompleted({ ...context, sent: outcome.sent, copies: outcome.copies, next: options.next });
};
